import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Block } from "../../api/types.ts";
import { BlockFields } from "./BlockFields.tsx";
import { BLOCK_LABELS } from "./blocks.ts";

/**
 * One block in the editor: drag handle, its property form, and the toggle
 * between the visual fields and hand-written HTML.
 */
export function BlockCard({
  block,
  selected,
  onSelect,
  onChange,
  onRemove,
}: {
  block: Block;
  selected: boolean;
  onSelect: () => void;
  onChange: (next: Block) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });

  const hasOverride = typeof block.rawHtml === "string";

  return (
    <div
      ref={setNodeRef}
      className={`block-card${selected ? " selected" : ""}${isDragging ? " dragging" : ""}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <div className="block-head">
        {/*
          dnd-kit binds keyboard sensors to the handle too, so reordering works
          without a mouse.
        */}
        <button
          type="button"
          className="grip"
          aria-label={`Reorder ${BLOCK_LABELS[block.type]} block`}
          {...attributes}
          {...listeners}
        >
          ⠿
        </button>
        <button type="button" className="block-title" onClick={onSelect}>
          {BLOCK_LABELS[block.type]}
          {hasOverride && <span className="pill warn">code</span>}
        </button>
        <button
          type="button"
          className="btn ghost small"
          onClick={() => {
            if (hasOverride) {
              // Dropping the override restores the visual rendering, because
              // props were kept while it was in place. The key is deleted
              // rather than set to undefined, since "absent" is what the API
              // reads as "no override".
              const { rawHtml: _dropped, ...rest } = block;
              onChange(rest);
            } else {
              onChange({ ...block, rawHtml: "" });
            }
          }}
        >
          {hasOverride ? "Use editor" : "Edit HTML"}
        </button>
        <button
          type="button"
          className="btn ghost small"
          onClick={onRemove}
          aria-label="Remove block"
        >
          ✕
        </button>
      </div>

      {selected && (
        <div className="block-body">
          {hasOverride ? (
            <div className="field">
              <label htmlFor={`raw-${block.id}`}>HTML for this block</label>
              <textarea
                id={`raw-${block.id}`}
                className="mono"
                rows={8}
                value={block.rawHtml ?? ""}
                onChange={(e) => onChange({ ...block, rawHtml: e.target.value })}
              />
              <div className="hint">
                Replaces this block only. The rest of the message keeps rendering normally, and
                switching back restores the fields above.
              </div>
            </div>
          ) : (
            <BlockFields block={block} onChange={onChange} />
          )}
        </div>
      )}
    </div>
  );
}
